import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

const DURATION = 620;
const EASING = 'cubic-bezier(.22,1,.36,1)';
/** Cuánto queda teñida la fila que se movió. */
const HIGHLIGHT_MS = 1100;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

interface FlipOptions {
  /** Clase para la fila que subió de puesto. */
  upClass?: string;
  /** Clase para la fila que bajó. */
  downClass?: string;
}

/**
 * Anima el cambio de puesto de las filas dentro de un contenedor.
 *
 * Usa FLIP: se recuerda dónde estaba cada fila, se deja que React reordene, se
 * mide dónde quedó y se la lleva de vuelta a su posición vieja con un
 * `transform` para animarla hasta cero. El navegador solo compone, así que no
 * hay relayout por cuadro por más filas que se muevan a la vez.
 *
 * Las filas deben llevar `data-flip-key` con un identificador estable.
 *
 * Se mide con `offsetTop` y no con `getBoundingClientRect`: este último es
 * relativo al viewport, así que un simple scroll entre dos renders se leería
 * como si todas las filas se hubieran movido.
 */
export function useFlipReorder(
  containerRef: RefObject<HTMLElement>,
  { upClass = 'rr-moved-up', downClass = 'rr-moved-down' }: FlipOptions = {}
) {
  const positions = useRef(new Map<string, number>());
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  // `useLayoutEffect` corre con el DOM ya actualizado pero antes de pintar: es
  // la única ventana para medir la posición nueva y corregirla sin parpadeo.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const nodes = Array.from(
      container.querySelectorAll<HTMLElement>('[data-flip-key]')
    );
    const reduced = prefersReducedMotion();

    nodes.forEach((node) => {
      const key = node.dataset.flipKey;
      if (!key) return;

      const after = node.offsetTop;
      const before = positions.current.get(key);
      positions.current.set(key, after);

      // Sin posición previa es una fila nueva: entra donde va, sin viaje.
      if (before === undefined || before === after || reduced) return;

      const delta = before - after;

      node.animate(
        [{ transform: `translateY(${delta}px)` }, { transform: 'none' }],
        { duration: DURATION, easing: EASING }
      );

      // `delta > 0` significa que la fila estaba más abajo: subió en la lista.
      const climbed = delta > 0;
      node.classList.add(climbed ? upClass : downClass);

      // Cada timeout se saca solo de la lista al dispararse. Drenarla nada más
      // que al desmontar la dejaba crecer con cada reordenamiento, y esta
      // pantalla se queda abierta el día entero refrescándose cada 30 segundos.
      const handle: ReturnType<typeof setTimeout> = setTimeout(() => {
        node.classList.remove(upClass, downClass);
        timeouts.current = timeouts.current.filter((id) => id !== handle);
      }, HIGHLIGHT_MS);

      timeouts.current.push(handle);
    });

    // Las filas que ya no están dejan de ocupar memoria.
    const alive = new Set(nodes.map((n) => n.dataset.flipKey));
    positions.current.forEach((_value, key) => {
      if (!alive.has(key)) positions.current.delete(key);
    });
  });

  useEffect(
    () => () => {
      timeouts.current.forEach(clearTimeout);
      timeouts.current = [];
    },
    []
  );
}
